// src/pages/Intake.jsx
//
// The print intake, /intake, reached from the Create popup's Intake tab.
//
// Steps follow the "Submit for Printing" mock-up, without the parts that are
// their own tickets: Format, Design and Proof (the formatting engine and the
// AI), and the rights, royalty and warranty sections (Rights & agreement).
//
//   Source → Your book | Files → Book specs → Review → Submitted
//
// Nothing is stored until Submit. A ReVisionari book is frozen by the server
// at that moment, and uploads travel with the submit itself. The rules this
// page mirrors are in backend/routes/intake.py.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { tokens } from '../theme';
import IntakeShell from '../components/intake/IntakeShell';
import IntakeStepper from '../components/intake/IntakeStepper';
import SourceChoices from '../components/intake/SourceChoices';
import FileDrop from '../components/intake/FileDrop';
import SpecsForm, { QuoteStrip } from '../components/intake/SpecsForm';
import ProofPreview from '../components/intake/ProofPreview';
import { readImageSize } from '../utils/imageInfo';
import BookPicker, { bookId, lastEdited } from '../components/intake/BookPicker';
import DetailRows from '../components/intake/DetailRows';
import useQuote from '../components/intake/useQuote';
import { intakeApi } from '../components/intake/api';
import {
  UPLOAD_RULES,
  canImport,
  estimatePages,
  fileProblem,
  isOpen,
  isPrintReady,
  isValidEmail,
  needsResave,
  newClientKey,
  openingSpecs,
  specRows,
} from '../utils/intake';
import {
  bookPrintOption,
  bookSizeLabel,
  coverAdvice,
  inches,
  offeredSizes,
  pageFits,
  specsNotes,
} from '../utils/printSizes';
import {
  SP,
  FONT,
  actionsSx,
  backButtonSx,
  labelSx,
  noteBoxSx,
  panelSubSx,
  panelSx,
  panelTitleSx,
  primaryButtonSx,
} from '../components/intake/intakeSx';

const SOURCE_STEP = 0;
const BOOK_STEP = 1;
const SPECS_STEP = 2;
const REVIEW_STEP = 3;
const DONE = 4;

// The mock-up's "what happens next" timeline, minus the steps that belong to
// the formatting and rights tickets.
const NEXT_STEPS = [
  { title: 'Submitted', text: 'Your book and print specs', state: 'done' },
  { title: 'ReVisionari review', text: 'We check your files and print specs', state: 'now' },
  { title: 'Approved, or changes requested', text: "If we need anything, you'll get a note and can resubmit" },
  { title: 'Final proof sign-off', text: 'You approve the print-ready proof' },
  { title: 'Printing and binding', text: 'Your copies go to press' },
];

function Spinner() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function ErrorText({ children }) {
  return (
    <Typography role="alert" sx={{ color: tokens.color.error, fontSize: 13, mt: 2 }}>
      {children}
    </Typography>
  );
}

// What the author should know about the specs before submitting, each with
// its one-click fix where there is one. Advice, not a gate: see specsNotes.
function SpecsNotes({ notes, onFix }) {
  if (!notes.length) return null;
  return (
    <Box sx={noteBoxSx(tokens.color.warning, tokens.color.warningTint)} role="status" data-testid="intake-spec-notes">
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1 }}>
        {notes.map((note) => (
          <Box component="li" key={note.text} sx={{ fontSize: 13 }}>
            {note.text}
            {note.fix && (
              <Button
                size="small"
                onClick={() => onFix(note.fix.specs)}
                sx={{ ml: 1, py: 0, textTransform: 'none', fontWeight: 600 }}
              >
                {note.fix.label}
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function NextSteps() {
  return (
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: '22px auto 0', maxWidth: 420, textAlign: 'left' }}>
      {NEXT_STEPS.map((s, i) => (
        <Box
          component="li"
          key={s.title}
          sx={{
            position: 'relative',
            pl: '28px',
            pb: '14px',
            fontSize: 13,
            color: SP.gray,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '6px',
              top: '4px',
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: `2px solid ${s.state === 'done' ? tokens.color.success : s.state === 'now' ? SP.gold : SP.lineSoft}`,
              background: s.state === 'done' ? tokens.color.success : s.state === 'now' ? SP.gold : SP.white,
            },
            ...(i < NEXT_STEPS.length - 1 && {
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '11px',
                top: '17px',
                bottom: 0,
                width: '1px',
                background: SP.lineSoft,
              },
            }),
          }}
        >
          <Box component="b" sx={{ display: 'block', color: SP.black, fontWeight: 600 }}>
            {s.title}
          </Box>
          {s.text}
        </Box>
      ))}
    </Box>
  );
}

export default function Intake() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromPopup = ['revisionari', 'upload'].includes(params.get('source')) ? params.get('source') : null;

  const [source, setSource] = useState(fromPopup);
  const [step, setStep] = useState(fromPopup ? BOOK_STEP : SOURCE_STEP);

  // A ReVisionari book.
  const [books, setBooks] = useState(null);
  const [booksError, setBooksError] = useState(null);
  const [openByDraft, setOpenByDraft] = useState({});
  const [book, setBook] = useState(null);
  const [bookDetail, setBookDetail] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);

  // Uploaded files.
  const [files, setFiles] = useState({ manuscript: null, cover: null });
  const [fileErrors, setFileErrors] = useState({});
  // What an uploaded PDF manuscript says about itself, read in the browser the
  // moment it is chosen so the specs can start from it. The counter drops a
  // read that finishes after its file was replaced.
  const [pdfInfo, setPdfInfo] = useState(null);
  const pdfRead = useRef(0);
  // Import: a non-PDF manuscript becomes a ReVisionari book, and the report
  // says what was found in it.
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importReport, setImportReport] = useState(null);
  // An uploaded cover's size as the browser reads it, and the cover this
  // book's specs need (null until asked; { cover: null } when only the team can say).
  const [coverInfo, setCoverInfo] = useState(null);
  const coverRead = useRef(0);
  const [coverNeed, setCoverNeed] = useState(null);
  // The proof tick belongs to one book (or file) at one size: change either
  // and it has to be given again.
  const [proofConfirmedFor, setProofConfirmedFor] = useState(null);

  // Book specs.
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [pagesFrom, setPagesFrom] = useState(null); // 'estimate' | 'pdf' | null
  const specsOpenedFor = useRef(null);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const { quote, error: quoteError } = useQuote(specs);

  // Submitting. One key for the whole visit: a retry after a dropped response
  // gets the first submission back instead of making a second.
  const clientKey = useRef(newClientKey());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  // A change of step scrolls back up to the stepper; arriving does not, or the
  // page opens partway down with its title and Back button out of view. Keyed
  // on the last step rather than a first-run flag, because StrictMode runs the
  // mount effect twice. And only once the step has rendered: scrolling while
  // the specs step was still a spinner left the author mid-form when the form
  // arrived. Optional-chained: jsdom has no scrollIntoView.
  const topRef = useRef(null);
  const scrolledFor = useRef(step);
  const stepReady = step !== SPECS_STEP || Boolean(specs && pricing) || Boolean(pricingError);
  useEffect(() => {
    if (!stepReady || scrolledFor.current === step) return;
    scrolledFor.current = step;
    topRef.current?.scrollIntoView?.({ block: 'start' });
  }, [step, stepReady]);

  // Leaving mid-way loses the choices and the files; say so first.
  const dirty = step > SOURCE_STEP && step < DONE && Boolean(book || files.manuscript || files.cover);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // The author's books, and which of them already have an open submission.
  useEffect(() => {
    if (source !== 'revisionari' || books !== null) return undefined;
    let cancelled = false;
    Promise.all([intakeApi.myBooks(), intakeApi.mySubmissions()])
      .then(([list, mine]) => {
        if (cancelled) return;
        const open = {};
        ((mine && mine.submissions) || []).forEach((s) => {
          if (s.draft_id && isOpen(s.status) && !open[s.draft_id]) open[s.draft_id] = s;
        });
        setOpenByDraft(open);
        setBooks(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (cancelled) return;
        setBooks([]);
        setBooksError("We couldn't load your books. Refresh the page to try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [source, books]);

  // The print options, once, when the specs step first opens.
  useEffect(() => {
    if (step !== SPECS_STEP || pricing) return undefined;
    let cancelled = false;
    intakeApi
      .pricing()
      .then((p) => {
        if (!cancelled) setPricing(p);
      })
      .catch(() => {
        if (!cancelled) setPricingError("We couldn't load the print options. Refresh the page to try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [step, pricing]);

  // Open the specs once per book, or per manuscript file. Going back and forth
  // keeps the author's changes; another book or file starts again, and so does
  // a PDF whose pages finish reading after Continue was pressed.
  const manuscriptKey = files.manuscript
    ? `${files.manuscript.name}:${files.manuscript.size}:${pdfInfo ? pdfInfo.status : 'none'}`
    : 'none';
  const specsKey = source === 'revisionari' ? `book:${book ? bookId(book) : ''}` : `upload:${manuscriptKey}`;
  useEffect(() => {
    if (step !== SPECS_STEP || !pricing || specsOpenedFor.current === specsKey) return;
    const pagesCat = (pricing.categories || []).find((c) => c.id === 'pages');
    let opening;
    let from = null;
    if (source === 'revisionari') {
      const bookDoc = bookDetail || book;
      const quoted = (bookDoc && bookDoc.print_spec && bookDoc.print_spec.selections) || {};
      // The order starts at the size the book is laid out at, so it prints the
      // way it previews. openingSpecs falls back when that size isn't offered.
      const own = bookPrintOption(bookDoc);
      opening = openingSpecs(pricing, own ? { ...quoted, size: own } : quoted);
      if (bookDetail && quoted.pages == null && pagesCat) {
        opening.pages = estimatePages(bookDetail.content, { min: pagesCat.min, max: pagesCat.max });
        from = 'estimate';
      }
    } else {
      opening = openingSpecs(pricing, {});
      if (pdfInfo && pdfInfo.status === 'ok') {
        const size = offeredSizes(pricing).find((id) => pageFits(pdfInfo.widthIn, pdfInfo.heightIn, id));
        if (size) opening.size = size;
        if (pagesCat && pdfInfo.pages >= pagesCat.min && pdfInfo.pages <= pagesCat.max) {
          opening.pages = pdfInfo.pages;
          from = 'pdf';
        }
      }
    }
    specsOpenedFor.current = specsKey;
    setSpecs(opening);
    setPagesFrom(from);
  }, [step, pricing, specsKey, source, book, bookDetail, pdfInfo]);

  let pagesNote;
  if (pagesFrom === 'pdf') {
    pagesNote = 'Read from your PDF.';
  } else if (source === 'upload') {
    pagesNote = isPrintReady(files.manuscript)
      ? "Enter your PDF's page count. We confirm it during review."
      : 'Your best estimate. We confirm it during review.';
  } else if (pagesFrom === 'estimate') {
    pagesNote = 'Estimated from your manuscript. Change it if you know the real count.';
  }

  // The cover this book needs, asked for whenever the specs change.
  useEffect(() => {
    if (source !== 'upload' || !specs) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      intakeApi
        .coverSize(specs)
        .then((data) => {
          if (!cancelled) setCoverNeed({ cover: data.cover });
        })
        .catch(() => {});
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [source, specs]);

  const advice =
    source === 'upload' && coverNeed
      ? coverAdvice({ need: coverNeed.cover, cover: coverInfo, specs })
      : { guide: null, notes: [] };
  const notes = specsNotes({
    source,
    specs,
    pdf: source === 'upload' ? pdfInfo : null,
    bookOption: source === 'revisionari' ? bookPrintOption(bookDetail || book) : null,
    pricing,
  }).concat(advice.notes);

  const proofKey =
    source === 'revisionari'
      ? `book:${book ? bookId(book) : ''}:${specs ? specs.size : ''}`
      : `file:${files.manuscript ? `${files.manuscript.name}:${files.manuscript.size}` : ''}`;
  const proofConfirmed = proofConfirmedFor === proofKey;

  let pdfDetail;
  if (pdfInfo && pdfInfo.status === 'reading') pdfDetail = 'Reading your PDF…';
  if (pdfInfo && pdfInfo.status === 'ok') pdfDetail = `${pdfInfo.pages} pages · ${inches(pdfInfo.widthIn, pdfInfo.heightIn)}`;
  if (pdfInfo && pdfInfo.status === 'unreadable') pdfDetail = "We couldn't read this PDF's pages. Our team will check it.";

  const chooseSource = (id) => {
    if (id === 'new') {
      // The Template Gallery, as in the mock-up. Its cards open the Create
      // popup on New Manuscript, which is where the ticket sends this choice.
      navigate('/templates');
      return;
    }
    if (id !== source) {
      setBook(null);
      setBookDetail(null);
      setFiles({ manuscript: null, cover: null });
      setFileErrors({});
      pdfRead.current += 1;
      setPdfInfo(null);
      coverRead.current += 1;
      setCoverInfo(null);
      specsOpenedFor.current = null;
    }
    setSource(id);
    setSubmitError(null);
    setStep(BOOK_STEP);
  };

  const continueWithBook = async () => {
    setBookLoading(true);
    try {
      setBookDetail(await intakeApi.book(bookId(book)));
    } catch {
      // Without the text there is no page estimate; the specs open on defaults.
      setBookDetail(null);
    } finally {
      setBookLoading(false);
      setStep(SPECS_STEP);
    }
  };

  // PDF.js loads only now, on the first PDF: it is large, and most authors
  // never upload one.
  const readManuscriptPdf = (file) => {
    pdfRead.current += 1;
    const ticket = pdfRead.current;
    if (!file || !isPrintReady(file)) {
      setPdfInfo(null);
      return;
    }
    setPdfInfo({ status: 'reading' });
    import('../utils/pdfInfo')
      .then(({ readPdfInfo }) => readPdfInfo(file))
      .then((info) => {
        if (pdfRead.current === ticket) setPdfInfo(info);
      })
      .catch(() => {
        if (pdfRead.current === ticket) setPdfInfo({ status: 'unreadable' });
      });
  };

  // A PDF cover knows its size in inches; a JPG or PNG only its pixels. A TIFF
  // the browser can't open is left to the team's checks.
  const readCover = (file) => {
    coverRead.current += 1;
    const ticket = coverRead.current;
    setCoverInfo(null);
    if (!file) return;
    const keep = (info) => {
      if (coverRead.current === ticket && info) setCoverInfo(info);
    };
    if (isPrintReady(file)) {
      import('../utils/pdfInfo')
        .then(({ readPdfInfo }) => readPdfInfo(file))
        .then((info) => keep(info.status === 'ok' ? { widthIn: info.widthIn, heightIn: info.heightIn } : null))
        .catch(() => {});
    } else if (/\.(jpe?g|png)$/i.test(file.name)) {
      readImageSize(file).then(keep);
    }
  };

  const pickFile = (kind, file) => {
    const problem = fileProblem(file, kind);
    setFileErrors((prev) => ({ ...prev, [kind]: problem }));
    setFiles((prev) => ({ ...prev, [kind]: problem ? null : file }));
    if (kind === 'manuscript') readManuscriptPdf(problem ? null : file);
    if (kind === 'cover') readCover(problem ? null : file);
  };
  const removeFile = (kind) => {
    setFiles((prev) => ({ ...prev, [kind]: null }));
    setFileErrors((prev) => ({ ...prev, [kind]: null }));
    if (kind === 'manuscript') readManuscriptPdf(null);
    if (kind === 'cover') readCover(null);
  };
  const filesReady = !fileProblem(files.manuscript, 'manuscript') && !fileProblem(files.cover, 'cover');
  const importable = canImport(files.manuscript);
  const resave = needsResave(files.manuscript);

  // A Word, text, EPUB or web page manuscript becomes a ReVisionari book, and
  // from then on this is the ReVisionari-book path with that book chosen.
  const runImport = async () => {
    setImporting(true);
    setImportError(null);
    try {
      const data = await intakeApi.importBook(files);
      setImportReport({ ...data.report, bookId: data.book._id });
      pdfRead.current += 1;
      setPdfInfo(null);
      coverRead.current += 1;
      setCoverInfo(null);
      setFiles({ manuscript: null, cover: null });
      setFileErrors({});
      specsOpenedFor.current = null;
      setBook(data.book);
      setBookDetail(null);
      setBooks(null); // reload the list, which now includes it
      setSource('revisionari');
      topRef.current?.scrollIntoView?.({ block: 'start' });
    } catch (err) {
      setImportError(err.status ? err.message : "We couldn't reach ReVisionari. Check your connection and try again.");
    } finally {
      setImporting(false);
    }
  };

  const reviewRows = useMemo(() => {
    const rows =
      source === 'revisionari'
        ? [{ label: 'Book', value: `${(book && book.title) || 'Untitled'} (from your ReVisionari library)` }]
        : [
            { label: 'Manuscript file', value: files.manuscript ? files.manuscript.name : '—' },
            { label: 'Cover file', value: files.cover ? files.cover.name : '—' },
          ];
    rows.push({ label: 'Contact email', value: email.trim() });
    return rows.concat(specRows(pricing, specs).map(({ label, value }) => ({ label, value })));
  }, [source, book, files, email, pricing, specs]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const payload = {
      client_key: clientKey.current,
      source,
      contact_email: email.trim(),
      specs,
      proof_confirmed: proofConfirmed,
      ...(source === 'revisionari' ? { draft_id: bookId(book) } : {}),
    };
    try {
      const data = await intakeApi.submit(payload, source === 'upload' ? files : null);
      setResult(data);
      setStep(DONE);
    } catch (err) {
      setSubmitError({
        message: err.status
          ? err.message
          : "We couldn't reach ReVisionari. Check your connection and try again.",
        reference: err.data && err.data.reservation_number,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ['Source', source === 'upload' ? 'Files' : 'Your book', 'Book specs', 'Review'];
  const reference = result && result.submission && result.submission.reservation_number;

  return (
    <IntakeShell
      eyebrow="§ Submit for printing"
      title="Get your book printed and bound"
      intro="Choose your book, confirm the print specs, and submit it to ReVisionari's production team for review."
      onBack={() => navigate(-1)}
    >
      <Box ref={topRef} sx={{ scrollMarginTop: 80 }} />
      {step < DONE && <IntakeStepper steps={steps} active={step} />}

      {step === SOURCE_STEP && (
        <Box sx={panelSx} data-testid="intake-step-source">
          <Typography component="h2" sx={panelTitleSx}>
            Where&rsquo;s your book coming from?
          </Typography>
          <Typography sx={panelSubSx}>Choose one to get started.</Typography>
          <Box sx={{ mt: 2.75 }}>
            <SourceChoices onChoose={chooseSource} />
          </Box>
        </Box>
      )}

      {step === BOOK_STEP && source === 'revisionari' && (
        <Box sx={panelSx} data-testid="intake-step-book">
          <Typography component="h2" sx={panelTitleSx}>
            Choose your book
          </Typography>
          <Typography sx={panelSubSx}>
            We&rsquo;ll use what&rsquo;s saved in ReVisionari when you submit, so there&rsquo;s nothing to upload.
          </Typography>

          {books === null && <Spinner />}
          {booksError && <ErrorText>{booksError}</ErrorText>}
          {books && !booksError && books.length === 0 && (
            <Box sx={{ ...noteBoxSx(SP.purpleSoft, SP.lavender) }}>
              <Typography sx={{ fontSize: 13.5 }}>You don&rsquo;t have any books in ReVisionari yet.</Typography>
              <Button onClick={() => navigate('/templates')} sx={{ mt: 1, px: 0, textTransform: 'none' }}>
                Start a new manuscript
              </Button>
            </Box>
          )}
          {books && books.length > 0 && (
            <BookPicker
              books={books}
              openByDraft={openByDraft}
              selectedId={book ? bookId(book) : null}
              onSelect={setBook}
            />
          )}

          {book && (
            <Box
              sx={{ mt: 2.5, border: `1.5px solid ${SP.purpleSoft}`, borderRadius: '7px', p: '18px 20px', background: SP.lavender }}
              data-testid="intake-selected-book"
            >
              <Typography sx={{ fontFamily: FONT.serif, fontWeight: 600, fontSize: 16, overflowWrap: 'anywhere' }}>
                {book.title || 'Untitled'}
              </Typography>
              <Typography sx={{ fontFamily: FONT.mono, fontSize: 11, color: SP.gray, mt: '6px' }}>
                {lastEdited(book)} · laid out at {bookSizeLabel(book)}
              </Typography>
              {importReport && importReport.bookId === bookId(book) && (
                <Box sx={{ mt: 1.5 }} data-testid="intake-import-report">
                  <Typography sx={{ fontSize: 13 }}>
                    <b>We turned {importReport.from} into this book:</b> {importReport.chapters}{' '}
                    {importReport.chapters === 1 ? 'chapter' : 'chapters'} ·{' '}
                    {importReport.words.toLocaleString('en-US')} words
                    {importReport.pictures > 0 &&
                      ` · ${importReport.pictures} ${importReport.pictures === 1 ? 'picture' : 'pictures'}`}
                    .
                  </Typography>
                  {importReport.chapters === 0 && (
                    <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                      We didn&rsquo;t find any chapter headings. Mark them in the editor so your book gets a
                      contents page.
                    </Typography>
                  )}
                  {importReport.contents_removed && (
                    <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                      We left out your typed table of contents. A real one is built from your chapters when the book
                      is printed.
                    </Typography>
                  )}
                  {importReport.pictures_skipped > 0 && (
                    <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                      {importReport.pictures_skipped}{' '}
                      {importReport.pictures_skipped === 1 ? "picture couldn't" : "pictures couldn't"} come across.
                      Add {importReport.pictures_skipped === 1 ? 'it' : 'them'} in the editor.
                    </Typography>
                  )}
                </Box>
              )}
              <Typography sx={{ fontSize: 13, mt: 1.5, maxWidth: '60ch' }}>
                {importReport && importReport.bookId === bookId(book)
                  ? 'Check it in the editor before you submit: chapters, pictures and anything unusual. '
                  : 'Is it ready to print? You can make changes first and come back. '}
                Once you submit, the book can&rsquo;t be changed unless the reviewer sends it back.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate(`/editor/${bookId(book)}`)}
                sx={{ mt: 1.5, textTransform: 'none' }}
                data-testid="intake-make-changes"
              >
                Make changes first
              </Button>
            </Box>
          )}

          <Box sx={actionsSx}>
            <Button sx={backButtonSx} onClick={() => setStep(SOURCE_STEP)}>
              &larr; Back
            </Button>
            <Button
              variant="contained"
              sx={primaryButtonSx}
              disabled={!book || bookLoading}
              onClick={continueWithBook}
              data-testid="intake-continue"
            >
              {bookLoading ? 'Opening…' : 'Continue to book specs'}
            </Button>
          </Box>
        </Box>
      )}

      {step === BOOK_STEP && source === 'upload' && (
        <Box sx={panelSx} data-testid="intake-step-files">
          <Typography component="h2" sx={panelTitleSx}>
            Upload your files
          </Typography>
          <Typography sx={panelSubSx}>
            Your manuscript and your cover, as two files. A print-ready PDF goes to review as it is. A Word, text,
            EPUB or web page file becomes a ReVisionari book you check before anything is printed.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75, mt: 2.5 }}>
            <FileDrop
              kind="manuscript"
              label="Manuscript file"
              hint="PDF, Word (.docx), EPUB, text, Markdown or web page · up to 50 MB"
              accept={UPLOAD_RULES.manuscript.extensions.join(',')}
              file={files.manuscript}
              error={fileErrors.manuscript}
              detail={pdfDetail}
              onFile={(f) => pickFile('manuscript', f)}
              onRemove={() => removeFile('manuscript')}
            />
            <FileDrop
              kind="cover"
              label="Cover file"
              hint="PDF (back, spine and front) preferred · JPG, PNG or TIFF · up to 50 MB"
              accept={UPLOAD_RULES.cover.extensions.join(',')}
              file={files.cover}
              error={fileErrors.cover}
              onFile={(f) => pickFile('cover', f)}
              onRemove={() => removeFile('cover')}
            />
          </Box>

          {files.manuscript && (
            <Box sx={noteBoxSx(SP.purpleSoft, SP.lavender)} data-testid="intake-path-note">
              <Typography sx={{ fontSize: 12.5 }}>
                {isPrintReady(files.manuscript) && (
                  <>
                    <b>Print-ready PDF.</b> It goes to review as it is, so check it against the list below.
                  </>
                )}
                {importable && (
                  <>
                    <b>We&rsquo;ll turn this into a ReVisionari book.</b> We find its chapters, then you check it in
                    the editor before anything is printed.
                  </>
                )}
                {resave && (
                  <>
                    <b>We can&rsquo;t read this format yet.</b> Save it as a Word document (.docx) and upload that,
                    or upload a print-ready PDF. In Word, Pages or OpenOffice: File, then Save As or Export, then
                    Word Document.
                  </>
                )}
              </Typography>
            </Box>
          )}

          <Box sx={noteBoxSx(SP.gold, SP.lavender)}>
            <Box sx={labelSx}>Uploading a print-ready PDF?</Box>
            <Box component="ul" sx={{ m: '9px 0 0 18px', p: 0, fontSize: 12.5, '& li': { mt: '4px' } }}>
              <li>0.125&Prime; bleed on all sides, fonts embedded, images at 300 DPI</li>
              <li>Saddle stitch needs a page count in multiples of 4</li>
              <li>
                Perfect binding needs a minimum spine width. Thin books under about 60 pages usually work better
                saddle-stitched.
              </li>
            </Box>
            <Typography sx={{ fontSize: 12, color: SP.gray, mt: 1.25 }}>
              Writing in Google Docs, Pages or Scrivener? Save your manuscript as Word or PDF first.
            </Typography>
          </Box>

          {importing && (
            <Typography role="status" sx={{ fontSize: 12.5, color: SP.gray, mt: 2 }}>
              Turning your file into a book. A long book with pictures can take a minute.
            </Typography>
          )}
          {importError && <ErrorText>{importError}</ErrorText>}

          <Box sx={actionsSx}>
            <Button sx={backButtonSx} onClick={() => setStep(SOURCE_STEP)} disabled={importing}>
              &larr; Back
            </Button>
            <Button
              variant="contained"
              sx={primaryButtonSx}
              disabled={!filesReady || resave || importing}
              onClick={importable ? runImport : () => setStep(SPECS_STEP)}
              data-testid="intake-continue"
            >
              {importing ? 'Turning it into a book…' : importable ? 'Turn it into a ReVisionari book' : 'Continue to book specs'}
            </Button>
          </Box>
        </Box>
      )}

      {step === SPECS_STEP && (
        <Box sx={panelSx} data-testid="intake-step-specs">
          <Typography component="h2" sx={panelTitleSx}>
            Book specs
          </Typography>
          <Typography sx={panelSubSx}>
            Everything is filled in with our standard options, so change anything you like. We just need a contact
            email.
          </Typography>

          {pricingError && <ErrorText>{pricingError}</ErrorText>}
          {!pricingError && !specs && <Spinner />}
          {specs && pricing && (
            <SpecsForm
              pricing={pricing}
              specs={specs}
              onChange={setSpecs}
              email={email}
              onEmailChange={setEmail}
              emailTouched={emailTouched}
              onEmailBlur={() => setEmailTouched(true)}
              pagesNote={pagesNote}
              quote={quote}
              quoteError={quoteError}
            />
          )}
          {advice.guide && (
            <Box sx={noteBoxSx(SP.purpleSoft, SP.lavender)} data-testid="intake-cover-guide">
              <Typography sx={{ fontSize: 13 }}>{advice.guide}</Typography>
            </Box>
          )}
          <SpecsNotes notes={notes} onFix={setSpecs} />

          <Box sx={actionsSx}>
            <Button sx={backButtonSx} onClick={() => setStep(BOOK_STEP)}>
              &larr; Back
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {specs && !isValidEmail(email) && (
                <Typography sx={{ fontSize: 11.5, color: SP.gray }}>Add a contact email to continue.</Typography>
              )}
              <Button
                variant="contained"
                sx={primaryButtonSx}
                disabled={!specs || !isValidEmail(email)}
                onClick={() => setStep(REVIEW_STEP)}
                data-testid="intake-to-review"
              >
                Review submission
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {step === REVIEW_STEP && (
        <Box sx={panelSx} data-testid="intake-step-review">
          <Typography component="h2" sx={panelTitleSx}>
            Review and submit
          </Typography>
          <Typography sx={panelSubSx}>
            Check everything below. When you submit, your book goes to ReVisionari&rsquo;s production team for review.
            Nothing is printed until they approve it.
          </Typography>

          <DetailRows rows={reviewRows} testId="intake-review-rows" />
          <QuoteStrip quote={quote} error={quoteError} />
          {advice.guide && (
            <Box sx={noteBoxSx(SP.purpleSoft, SP.lavender)} data-testid="intake-cover-guide">
              <Typography sx={{ fontSize: 13 }}>{advice.guide}</Typography>
            </Box>
          )}
          <SpecsNotes notes={notes} onFix={setSpecs} />

          <ProofPreview
            draftId={source === 'revisionari' && book ? bookId(book) : null}
            file={source === 'upload' ? files.manuscript : null}
            size={specs ? specs.size : null}
            confirmed={proofConfirmed}
            onConfirm={(yes) => setProofConfirmedFor(yes ? proofKey : null)}
          />

          <Box sx={noteBoxSx(tokens.color.warning, tokens.color.warningTint)}>
            <Typography sx={{ fontSize: 13 }}>
              After you submit, your book can&rsquo;t be changed unless the reviewer sends it back.
            </Typography>
          </Box>

          {/* A 49 MB upload took over a minute in the live check, with only the
              button label to say anything was happening. */}
          {submitting && source === 'upload' && (
            <Typography role="status" sx={{ fontSize: 12.5, color: SP.gray, mt: 2 }}>
              Uploading your files. Large files can take a minute or two, so keep this page open.
            </Typography>
          )}

          {submitError && (
            <ErrorText>
              {submitError.message}{' '}
              {submitError.reference && (
                <Link component={RouterLink} to={`/intake/${submitError.reference}`}>
                  Open {submitError.reference}
                </Link>
              )}
            </ErrorText>
          )}

          <Box sx={actionsSx}>
            <Button sx={backButtonSx} onClick={() => setStep(SPECS_STEP)} disabled={submitting}>
              &larr; Back
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {!proofConfirmed && (
                <Typography sx={{ fontSize: 11.5, color: SP.gray }}>Preview your book and tick the box to submit.</Typography>
              )}
              <Button
                variant="contained"
                sx={primaryButtonSx}
                onClick={submit}
                disabled={submitting || !proofConfirmed}
                data-testid="intake-submit"
              >
                {submitting ? 'Submitting…' : 'Submit for review'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {step === DONE && reference && (
        <Box sx={{ ...panelSx, textAlign: 'center' }} data-testid="intake-confirmation">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: tokens.color.success,
              color: SP.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mt: 1,
            }}
          >
            <CheckIcon />
          </Box>
          <Typography component="h2" sx={{ ...panelTitleSx, fontSize: 22, mt: 2.25 }}>
            Submitted for review
          </Typography>
          <Typography
            sx={{ fontFamily: FONT.mono, fontSize: 13, color: tokens.color.goldInk, mt: 1 }}
            data-testid="intake-reservation"
          >
            Reservation number: {reference}
          </Typography>
          <Typography sx={{ color: SP.gray, fontSize: 13.5, mt: 1.5, maxWidth: '48ch', mx: 'auto' }}>
            ReVisionari&rsquo;s production team will review your book within 1&ndash;2 business days.{' '}
            {result.submission.email_enabled
              ? "We'll email you when it's approved, or if anything needs a change."
              : 'Check your submission page for their answer.'}
          </Typography>
          {result.email_sent && (
            <Typography sx={{ color: SP.gray, fontSize: 12.5, mt: 1 }}>
              We&rsquo;ve sent a copy to {email.trim()}.
            </Typography>
          )}
          <NextSteps />
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
            <Button variant="contained" component={RouterLink} to={`/intake/${reference}`} sx={primaryButtonSx}>
              View your submission
            </Button>
            <Button variant="outlined" onClick={() => navigate('/editor-home')} sx={{ textTransform: 'none' }}>
              Back to your writing
            </Button>
          </Box>
        </Box>
      )}
    </IntakeShell>
  );
}
